/**
 * Questo modulo gestisce il download dei log di lavoro dei modellisti in formato Excel.
 * 
 * Funzionamento:
 * 1. Gestisce la generazione di report dettagliati sul tempo di lavoro
 * 2. Calcola i tempi di lavoro considerando:
 *    - Orario lavorativo (9:00-18:00)
 *    - Pausa pranzo (13:30-14:30)
 *    - Weekend e festività italiane
 *    - Fusi orari e timestamp
 * 
 * Funzionalità principali:
 * - Calcolo preciso del tempo di lavoro effettivo
 * - Gestione delle festività italiane
 * - Generazione report Excel con:
 *   • Riepilogo per modellista
 *   • Dettaglio dei tempi di lavoro
 *   • Statistiche aggregate
 * - Formattazione automatica del file Excel
 * 
 * Output:
 * - File Excel con nome "ARShades_Log_[data].xlsx"
 * - Multiple sheets per diversi tipi di analisi
 */

import React, { useState } from "react";
import { Button, message } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import MediumModal from "../../components/Components/Modals/MediumModal";
import * as XLSX from 'xlsx';
import { db } from "../../data/base";
import { collection, getDocs, Timestamp, query, where } from "firebase/firestore";

const DownloadLog = ({ filters }) => {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // Apre la modale di download
    const showModal = () => {
        setIsModalVisible(true);
    };

    // Chiude la modale di download
    const handleCloseModal = () => {
        setIsModalVisible(false);
    };

    // Funzione per calcolare se una data è un giorno festivo
    const isHoliday = (date) => {
        const holidays = getItalianHolidays(date.getFullYear());
        return holidays.some(holiday =>
            holiday.getDate() === date.getDate() &&
            holiday.getMonth() === date.getMonth()
        );
    };

    // Funzione per calcolare le festività italiane
    const getItalianHolidays = (year) => {
        const easter = calculateEaster(year);
        const easterMonday = new Date(easter);
        easterMonday.setDate(easter.getDate() + 1);

        return [
            new Date(year, 0, 1),  // Capodanno
            new Date(year, 0, 6),  // Epifania
            easter,                // Pasqua
            easterMonday,          // Lunedì dell'Angelo
            new Date(year, 3, 25), // Festa della Liberazione
            new Date(year, 4, 1),  // Festa dei Lavoratori
            new Date(year, 5, 2),  // Festa della Repubblica
            new Date(year, 7, 15), // Ferragosto
            new Date(year, 10, 1), // Ognissanti
            new Date(year, 11, 8), // Immacolata Concezione
            new Date(year, 11, 25),// Natale
            new Date(year, 11, 26) // Santo Stefano
        ];
    };

    // Funzione per calcolare la data di Pasqua
    const calculateEaster = (year) => {
        const f = Math.floor,
            // Anno
            G = year % 19,
            C = f(year / 100),
            H = (C - f(C / 4) - f((8 * C + 13) / 25) + 19 * G + 15) % 30,
            I = H - f(H / 28) * (1 - f(29 / (H + 1)) * f((21 - G) / 11)),
            J = (year + f(year / 4) + I + 2 - C + f(C / 4)) % 7,
            L = I - J,
            month = 3 + f((L + 40) / 44),
            day = L + 28 - 31 * f(month / 4);
        return new Date(year, month - 1, day);
    };

    // Calcola il tempo di lavoro tra due date rispettando l'orario lavorativo
    const calculateWorkTime = (startDate, endDate) => {
        if (!startDate || !endDate) return "N/A";

        try {
            let start, end;

            // Gestione di diversi formati di timestamp
            if (typeof startDate === 'object' && startDate._seconds) {
                start = new Date(startDate._seconds * 1000);
            } else if (typeof startDate === 'object' && startDate.seconds) {
                start = new Date(startDate.seconds * 1000);
            } else if (startDate instanceof Date) {
                start = startDate;
            } else {
                return "N/A";
            }

            if (typeof endDate === 'object' && endDate._seconds) {
                end = new Date(endDate._seconds * 1000);
            } else if (typeof endDate === 'object' && endDate.seconds) {
                end = new Date(endDate.seconds * 1000);
            } else if (endDate instanceof Date) {
                end = endDate;
            } else {
                return "N/A";
            }

            // Se la data di fine è precedente alla data di inizio, restituisci N/A
            if (end < start) return "N/A";

            // Costanti per orari lavorativi
            const WORK_START_HOUR = 9; // 9:00
            const WORK_END_HOUR = 18; // 18:00
            const LUNCH_START_HOUR = 13.5; // 13:30
            const LUNCH_END_HOUR = 14.5; // 14:30
            const MS_PER_HOUR = 3600000; // millisecondi in un'ora

            let totalWorkTimeMs = 0;
            let currentDate = new Date(start);

            // Imposta l'ora di inizio al primo orario lavorativo disponibile
            if (currentDate.getHours() < WORK_START_HOUR) {
                currentDate.setHours(WORK_START_HOUR, 0, 0, 0);
            } else if (currentDate.getHours() >= WORK_END_HOUR) {
                // Se inizia dopo l'orario lavorativo, passa al giorno successivo
                currentDate.setDate(currentDate.getDate() + 1);
                currentDate.setHours(WORK_START_HOUR, 0, 0, 0);
            }

            // Continua finché non raggiungiamo la data di fine
            while (currentDate < end) {
                const dayOfWeek = currentDate.getDay();

                // Salta i weekend e i giorni festivi
                if (dayOfWeek === 0 || dayOfWeek === 6 || isHoliday(currentDate)) {
                    currentDate.setDate(currentDate.getDate() + 1);
                    currentDate.setHours(WORK_START_HOUR, 0, 0, 0);
                    continue;
                }

                // Calcola la fine della giornata lavorativa corrente o la data di fine
                let workDayEnd = new Date(currentDate);
                workDayEnd.setHours(WORK_END_HOUR, 0, 0, 0);

                // Se la data di fine è prima della fine della giornata lavorativa corrente
                if (end < workDayEnd) {
                    workDayEnd = new Date(end);
                }

                // Gestione della pausa pranzo
                if (currentDate.getHours() < LUNCH_START_HOUR && workDayEnd.getHours() >= LUNCH_END_HOUR) {
                    // Aggiungi il tempo fino alla pausa pranzo
                    const timeBeforeLunch = new Date(currentDate);
                    timeBeforeLunch.setHours(LUNCH_START_HOUR, 0, 0, 0);
                    totalWorkTimeMs += timeBeforeLunch - currentDate;

                    // Aggiungi il tempo dopo la pausa pranzo fino alla fine della giornata
                    const timeAfterLunch = new Date(currentDate);
                    timeAfterLunch.setHours(LUNCH_END_HOUR, 0, 0, 0);

                    if (workDayEnd > timeAfterLunch) {
                        totalWorkTimeMs += workDayEnd - timeAfterLunch;
                    }
                }
                // Se l'orario di lavoro va dalla pausa pranzo alla fine della giornata
                else if (currentDate.getHours() >= LUNCH_END_HOUR && workDayEnd.getHours() >= LUNCH_END_HOUR) {
                    totalWorkTimeMs += workDayEnd - currentDate;
                }
                // Se l'orario di lavoro è tutto prima della pausa pranzo
                else if (currentDate.getHours() < LUNCH_START_HOUR && workDayEnd.getHours() <= LUNCH_START_HOUR) {
                    totalWorkTimeMs += workDayEnd - currentDate;
                }
                // Se l'orario di lavoro è interamente durante la pausa pranzo
                else if (currentDate.getHours() >= LUNCH_START_HOUR && workDayEnd.getHours() <= LUNCH_END_HOUR) {
                    // Non contiamo questo tempo
                }

                // Passa al giorno successivo
                if (workDayEnd >= end) {
                    break;
                } else {
                    currentDate = new Date(currentDate);
                    currentDate.setDate(currentDate.getDate() + 1);
                    currentDate.setHours(WORK_START_HOUR, 0, 0, 0);
                }
            }

            // Converti i millisecondi in un formato leggibile
            const MS_PER_MINUTE = 60000;
            const MS_PER_SECOND = 1000;
            const HOURS_PER_DAY = 24;

            // Calcola i valori per le diverse unità di tempo
            let totalSeconds = Math.floor((totalWorkTimeMs % MS_PER_MINUTE) / MS_PER_SECOND);
            let totalMinutes = Math.floor((totalWorkTimeMs % MS_PER_HOUR) / MS_PER_MINUTE);
            let totalHours = Math.floor((totalWorkTimeMs % (MS_PER_HOUR * HOURS_PER_DAY)) / MS_PER_HOUR);
            let totalDays = Math.floor(totalWorkTimeMs / (MS_PER_HOUR * HOURS_PER_DAY));

            // Formatta la stringa di output
            return `${totalDays}d ${totalHours}h ${totalMinutes}m ${totalSeconds}s`;

        } catch (error) {
            console.error("Errore nel calcolo del tempo di lavoro:", error);
            return "Errore";
        }
    };

    // Formatta la data per la visualizzazione
    const formatDate = (timestamp) => {
        if (!timestamp) return "N/A";

        try {
            let date;

            if (typeof timestamp === 'object' && timestamp._seconds) {
                date = new Date(timestamp._seconds * 1000);
            } else if (typeof timestamp === 'object' && timestamp.seconds) {
                date = new Date(timestamp.seconds * 1000);
            } else if (timestamp instanceof Date) {
                date = timestamp;
            } else {
                return "N/A";
            }

            return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
        } catch (error) {
            console.error("Errore nella formattazione della data:", error);
            return "Errore Data";
        }
    };

    // Formatta la data per il nome del file
    const formatDateForFileName = (date) => {
        if (!date) return '';
        return `${date.getDate().toString().padStart(2, '0')}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getFullYear()}`;
    };

    // Converti un tempo di lavoro in millisecondi
    const workTimeToMs = (workTimeStr) => {
        if (!workTimeStr || workTimeStr === "N/A" || workTimeStr === "Errore") return 0;

        try {
            const regex = /(\d+)d\s+(\d+)h\s+(\d+)m\s+(\d+)s/;
            const match = workTimeStr.match(regex);

            if (!match) return 0;

            const days = parseInt(match[1], 10);
            const hours = parseInt(match[2], 10);
            const minutes = parseInt(match[3], 10);
            const seconds = parseInt(match[4], 10);

            const MS_PER_SECOND = 1000;
            const MS_PER_MINUTE = 60 * MS_PER_SECOND;
            const MS_PER_HOUR = 60 * MS_PER_MINUTE;
            const MS_PER_DAY = 24 * MS_PER_HOUR;

            return days * MS_PER_DAY + hours * MS_PER_HOUR + minutes * MS_PER_MINUTE + seconds * MS_PER_SECOND;
        } catch (error) {
            console.error("Errore nella conversione del tempo di lavoro:", error);
            return 0;
        }
    };

    // Converti millisecondi in un formato leggibile
    const msToWorkTime = (totalMs) => {
        if (totalMs === 0) return "0d 0h 0m 0s";

        const MS_PER_SECOND = 1000;
        const MS_PER_MINUTE = 60 * MS_PER_SECOND;
        const MS_PER_HOUR = 60 * MS_PER_MINUTE;
        const MS_PER_DAY = 24 * MS_PER_HOUR;

        const days = Math.floor(totalMs / MS_PER_DAY);
        totalMs %= MS_PER_DAY;

        const hours = Math.floor(totalMs / MS_PER_HOUR);
        totalMs %= MS_PER_HOUR;

        const minutes = Math.floor(totalMs / MS_PER_MINUTE);
        totalMs %= MS_PER_MINUTE;

        const seconds = Math.floor(totalMs / MS_PER_SECOND);

        return `${days}d ${hours}h ${minutes}m ${seconds}s`;
    };

    // Recupera e formatta i log per ogni modellista
    const fetchLogsForExport = async () => {
        const { dateRange } = filters;

        if (!dateRange || dateRange.length !== 2) {
            message.error("No data available for export");
            return null;
        }

        try {
            setIsLoading(true);

            // Configura le date iniziale e finale per includere tutto il giorno
            const dataInizio = new Date(dateRange[0]);
            const dataFine = new Date(dateRange[1]);

            dataInizio.setHours(0, 0, 0, 0);
            dataFine.setHours(23, 59, 59, 999);

            const timestampInizio = Timestamp.fromDate(dataInizio);
            const timestampFine = Timestamp.fromDate(dataFine);

            // Query per ottenere i log nell'intervallo di date
            const logRef = collection(db, "LogVariant");
            const logsSnapshot = await getDocs(logRef);

            // Recupera tutti i log nell'intervallo di date
            const logsData = [];
            logsSnapshot.forEach((doc) => {
                const data = doc.data();

                // Verifica se logInitVariantDate OPPURE logCompleteVariantDate è nell'intervallo
                const initDateInRange = data.logInitVariantDate &&
                    data.logInitVariantDate >= timestampInizio &&
                    data.logInitVariantDate <= timestampFine;

                const completeDateInRange = data.logCompleteVariantDate &&
                    data.logCompleteVariantDate >= timestampInizio &&
                    data.logCompleteVariantDate <= timestampFine;

                // Aggiungi il log se almeno una delle due date è nel range
                if (initDateInRange || completeDateInRange) {
                    logsData.push({
                        id: doc.id,
                        ...data
                    });
                }
            });

            // Recupera tutti i modellisti
            const profilesRef = collection(db, "Profiles");
            const modellistiQuery = query(profilesRef, where("role", "==", "Modellista"));
            const modellistiSnapshot = await getDocs(modellistiQuery);

            const modellisti = [];
            modellistiSnapshot.forEach((doc) => {
                const data = doc.data();
                if (data._email) {
                    modellisti.push({
                        email: data._email,
                        nome: `${data.firstName || ''} ${data.lastName || ''}`.trim() || data._email
                    });
                }
            });

            return { logs: logsData, modellisti };
        } catch (error) {
            console.error("Errore nel recupero dei dati:", error);
            message.error("An error occurred while retrieving data. Please try again later.");
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    // Organizziamo i dati per modellista
    const organizeDataByModeller = (logs, modellisti) => {
        // Inizializza l'oggetto dati per ogni modellista
        const modellistiData = {};

        modellisti.forEach(modellista => {
            modellistiData[modellista.email] = {
                modellista: modellista,
                logs: [],
                stats: {
                    totalLogs: 0,
                    totalFirstValidationTimeMs: 0, // in millisecondi
                }
            };
        });

        // Distribuisci i log ai rispettivi modellisti
        logs.forEach(log => {
            if (log.list_variant_assigned && log.list_variant_assigned.length > 0) {
                const modellistaEmail = log.list_variant_assigned[log.list_variant_assigned.length - 1].assignedAccount;

                if (modellistiData[modellistaEmail]) {
                    modellistiData[modellistaEmail].logs.push(log);
                    modellistiData[modellistaEmail].stats.totalLogs++;

                    // Calcola il tempo della prima validazione se disponibile
                    if (log.list_validate_variant && log.list_validate_variant.length > 0 && log.logInitVariantDate) {
                        const firstValidation = log.list_validate_variant[0].time;
                        const firstValidationTime = calculateWorkTime(
                            log.logInitVariantDate,
                            firstValidation
                        );

                        // Aggiungi questo tempo al totale in millisecondi
                        const msTime = workTimeToMs(firstValidationTime);
                        modellistiData[modellistaEmail].stats.totalFirstValidationTimeMs =
                            (modellistiData[modellistaEmail].stats.totalFirstValidationTimeMs || 0) + msTime;
                    }
                }
            }
        });

        // Converti il tempo totale da millisecondi a formato leggibile
        Object.values(modellistiData).forEach(data => {
            if (data.stats.totalFirstValidationTimeMs) {
                data.stats.firstValidationTime = msToWorkTime(data.stats.totalFirstValidationTimeMs);
            } else {
                data.stats.firstValidationTime = "N/A";
            }
        });

        return modellistiData;
    };

    // Crea fogli Excel per ogni modellista
    const createExcelSheets = (modellistiData) => {
        const workbook = XLSX.utils.book_new();

        // Raccogli tutti i log e statistiche per il foglio di riepilogo
        const allLogs = [];
        let totalGlasses = 0;
        let modellerCount = 0;

        // Per ogni modellista, crea un foglio
        Object.values(modellistiData).forEach(data => {
            // Filtra i log per includere solo quelli completati (non "In Progress")
            const completedLogs = data.logs.filter(log => log.logStatus !== "Pending");

            if (completedLogs.length === 0) return; // Skip modellisti senza log completati

            const { modellista } = data;
            modellerCount++;
            totalGlasses += completedLogs.length;

            // Aggiungi i log all'array di tutti i log
            completedLogs.forEach(log => allLogs.push({ ...log, modellerEmail: modellista.email, modellerName: modellista.nome }));

            // Raccogli statistiche per questo modellista (solo log completati)
            const completedLogsCount = completedLogs.filter(log => log.logStatus === "Complete").length;
            const declinedBySpaarkly = completedLogs.filter(log =>
                log.list_decline_variant &&
                log.list_decline_variant.some(d => d && (d.role === "ModellerSupervisor" || d.role === "Admin"))
            ).length;
            const declinedByClient = completedLogs.filter(log =>
                log.list_decline_variant &&
                log.list_decline_variant.some(d => d && d.role === "Cliente")
            ).length;

            // Calcola il tempo di prima validazione per i log completati
            let totalFirstValidationTimeMs = 0;
            completedLogs.forEach(log => {
                if (log.list_validate_variant && log.list_validate_variant.length > 0 && log.logInitVariantDate) {
                    const firstValidation = log.list_validate_variant[0].time;
                    const firstValidationTime = calculateWorkTime(
                        log.logInitVariantDate,
                        firstValidation
                    );
                    const msTime = workTimeToMs(firstValidationTime);
                    totalFirstValidationTimeMs += msTime;
                }
            });

            const firstValidationTime = msToWorkTime(totalFirstValidationTimeMs);

            // Crea la riga di intestazione con le statistiche riassuntive
            const summaryRow = [
                ['Modeller', modellista.email],
                ['Name', modellista.nome],
                ['Total number of glasses', completedLogs.length],
                ['Declined by Spaarkly', declinedBySpaarkly],
                ['Declined by Client', declinedByClient],
                ['Total first validation time', firstValidationTime || "N/A"],
                [''] // Riga vuota
            ];

            // Crea le righe per la tabella dei log con le stesse colonne di Log.js
            const headers = [
                'Product',
                'SKU',
                /* 'ID', */
                'Status',
                'Type of Modeling',
                'Modeling Note',
                'Completion Date',
                'Work Time First Validation',
                'Start Initialization Date',
                'End First Validation Date',
                'Work Complete Time',
                'Decline by Spaarkly',
                'Decline by Client'
            ];

            // Aggiungi l'intestazione della tabella
            const tableRows = [headers];

            // Aggiungi i dati per ogni log completato
            completedLogs.forEach(log => {
                // Calcola il tempo di prima validazione
                let firstValidationWork = "N/A";
                if (log.list_validate_variant && log.list_validate_variant.length > 0 && log.logInitVariantDate) {
                    const firstValidation = log.list_validate_variant[0].time;
                    firstValidationWork = calculateWorkTime(log.logInitVariantDate, firstValidation);
                }

                // Calcola il tempo di completamento
                let workComplete = "N/A";
                if (log.logInitVariantDate && log.logCompleteVariantDate) {
                    workComplete = calculateWorkTime(log.logInitVariantDate, log.logCompleteVariantDate);
                }

                // Formatta lo stato
                let statusText = "N/A";
                if (log.logStatus === "Complete") {
                    statusText = "Completed";
                } else {
                    statusText = log.logStatus || "Unknown";
                }

                const row = [
                    log.productName || 'N/A',
                    log.sku || 'N/A',
                    /* log.id || 'N/A', */
                    statusText,
                    log.list_validate_variant && log.list_validate_variant.length > 0 && log.list_validate_variant[0].type_of_modeling ? log.list_validate_variant[0].type_of_modeling : 'N/A',
                    log.list_validate_variant && log.list_validate_variant.length > 0 && log.list_validate_variant[0].modeling_note ? log.list_validate_variant[0].modeling_note : 'N/A',
                    formatDate(log.logCompleteVariantDate),
                    firstValidationWork,
                    formatDate(log.logInitVariantDate),
                    log.list_validate_variant && log.list_validate_variant.length > 0 ? formatDate(log.list_validate_variant[0].time) : 'N/A',
                    workComplete,
                    log.list_decline_variant ? log.list_decline_variant.filter(d => d && (d.role === "ModellerSupervisor" || d.role === "Admin")).length : 0,
                    log.list_decline_variant ? log.list_decline_variant.filter(d => d && d.role === "Cliente").length : 0
                ];

                tableRows.push(row);
            });

            // Unisci i dati riassuntivi e la tabella
            const sheetData = [...summaryRow, ...tableRows];

            // Crea il foglio Excel
            const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

            // Personalizza la larghezza delle colonne
            const colWidths = [
                { wch: 22 }, // A - Product
                { wch: 18 }, // B - SKU
                /* { wch: 22 }, // C - ID */
                { wch: 12 }, // C - Status
                { wch: 20 }, // D - Type of Modeling
                { wch: 25 }, // E - Modeling Note
                { wch: 20 }, // F - Completion Date
                { wch: 22 }, // G - Work Time First Validation
                { wch: 22 }, // H - Start Initialization Date
                { wch: 22 }, // I - End First Validation Date
                { wch: 20 }, // J - Work Complete Time
                { wch: 20 }, // K - Decline by Spaarkly
                { wch: 20 }  // L - Decline by Client
            ];

            worksheet['!cols'] = colWidths;

            // Aggiungi stili per migliorare la leggibilità

            // Stile per le intestazioni del riepilogo (A1:A8)
            for (let r = 0; r < 8; r++) {
                const cellRef = XLSX.utils.encode_cell({ r: r, c: 0 });
                if (!worksheet[cellRef]) continue;
                worksheet[cellRef].s = {
                    font: { bold: true, sz: 14 },
                    alignment: { vertical: "center", horizontal: "left" }
                };
            }

            // Stile per i valori del riepilogo (B1:B8)
            for (let r = 0; r < 8; r++) {
                const cellRef = XLSX.utils.encode_cell({ r: r, c: 1 });
                if (!worksheet[cellRef]) continue;
                worksheet[cellRef].s = {
                    font: { sz: 14 },
                    alignment: { vertical: "center", horizontal: "left" }
                };
            }

            // Stile per l'intestazione della tabella (riga 10)
            const headerRow = 9; // La riga 10 in base 0

            // Stile per l'intestazione della tabella
            for (let c = 0; c < headers.length; c++) {
                const cellRef = XLSX.utils.encode_cell({ r: headerRow, c: c });
                if (!worksheet[cellRef]) continue;
                worksheet[cellRef].s = {
                    font: { bold: true, color: { rgb: "FFFFFF" }, sz: 12 },
                    fill: { fgColor: { rgb: "4F81BD" } },
                    alignment: { vertical: "center", horizontal: "center" },
                    border: {
                        top: { style: "thin", color: { rgb: "000000" } },
                        bottom: { style: "thin", color: { rgb: "000000" } },
                        left: { style: "thin", color: { rgb: "000000" } },
                        right: { style: "thin", color: { rgb: "000000" } }
                    }
                };
            }

            // Aggiungi bordi alle celle della tabella
            for (let r = headerRow + 1; r < sheetData.length; r++) {
                // Identifica se questa riga è un'intestazione (confrontala con le intestazioni originali)
                const isHeader = JSON.stringify(sheetData[r]) === JSON.stringify(headers);

                // Identifica se questa riga contiene i separatori "───────────────────────" (vecchie linee rosse)
                // o "━━━━━━━━━━━━━━━━━━━━━━" (linee nere)
                const isSeparator = sheetData[r].some(cell =>
                    (typeof cell === 'string' && (cell.includes("──────") || cell.includes("━━━━")))
                );

                // Non abbiamo più bisogno di distinguere tra tipi di separatori, poiché ora sono tutti uguali
                if (isSeparator) {
                    for (let c = 0; c < headers.length; c++) {
                        const cellRef = XLSX.utils.encode_cell({ r: r, c: c });
                        if (!worksheet[cellRef]) continue;

                        worksheet[cellRef].s = {
                            font: { color: { rgb: "000000" }, bold: true },
                            alignment: { horizontal: "center" },
                            border: {
                                top: { style: "thick", color: { rgb: "000000" } },
                                bottom: { style: "thick", color: { rgb: "000000" } },
                                left: { style: "none" },
                                right: { style: "none" }
                            }
                        };
                    }
                    continue;
                }

                // Identifica se questa riga è parte di un riepilogo statistico di un modellista
                // Confronta la prima cella con "Modeller" per identificare l'inizio di un riepilogo
                const isModellerId = sheetData[r][0] === "Modeller";

                // Se è l'inizio di un riepilogo, applica stili speciali alle prossime 8 righe (il riepilogo completo)
                if (isModellerId) {
                    // Applica stili alle righe del riepilogo (fino a 8 righe, inclusa la riga vuota finale)
                    for (let i = 0; i < 8 && (r + i) < sheetData.length; i++) {
                        for (let c = 0; c < 2; c++) {
                            const cellRef = XLSX.utils.encode_cell({ r: r + i, c: c });
                            if (!worksheet[cellRef]) continue;

                            let style = {
                                border: {
                                    top: { style: i === 0 ? "medium" : "thin", color: { rgb: "4F81BD" } },
                                    bottom: { style: i === 7 ? "medium" : "thin", color: { rgb: "4F81BD" } },
                                    left: { style: c === 0 ? "medium" : "thin", color: { rgb: "4F81BD" } },
                                    right: { style: c === 1 ? "medium" : "thin", color: { rgb: "4F81BD" } }
                                }
                            };

                            // Aggiungi formato grassetto per le etichette e dimensione font
                            if (c === 0) {
                                style.font = { bold: true, sz: 14 };
                                style.alignment = { vertical: "center", horizontal: "left" };
                            } else {
                                style.font = { sz: 14 };
                                style.alignment = { vertical: "center", horizontal: "left" };
                            }

                            worksheet[cellRef].s = style;
                        }
                    }

                    // Salta le righe appena formattate
                    r += 7; // Dopo questo il ciclo incrementerà r di 1, per un totale di 8 righe saltate
                    continue;
                }

                // Per le altre righe, applica gli stili standard
                for (let c = 0; c < headers.length; c++) {
                    const cellRef = XLSX.utils.encode_cell({ r: r, c: c });
                    if (!worksheet[cellRef]) continue;

                    // Stile per le intestazioni delle colonne (che ora possono apparire più volte)
                    if (isHeader) {
                        worksheet[cellRef].s = {
                            font: { bold: true, color: { rgb: "FFFFFF" }, sz: 12 },
                            fill: { fgColor: { rgb: "4F81BD" } },
                            alignment: { vertical: "center", horizontal: "center" },
                            border: {
                                top: { style: "thin", color: { rgb: "000000" } },
                                bottom: { style: "thin", color: { rgb: "000000" } },
                                left: { style: "thin", color: { rgb: "000000" } },
                                right: { style: "thin", color: { rgb: "000000" } }
                            }
                        };
                        continue;
                    }

                    let style = {
                        border: {
                            top: { style: "thin", color: { rgb: "D3D3D3" } },
                            bottom: { style: "thin", color: { rgb: "D3D3D3" } },
                            left: { style: "thin", color: { rgb: "D3D3D3" } },
                            right: { style: "thin", color: { rgb: "D3D3D3" } }
                        }
                    };

                    // Allineamento specifico per alcune colonne
                    if (c === 3) { // Status column
                        style.alignment = { horizontal: "center" };
                    } else if (c >= 4 && c <= 9) { // Time and date columns
                        style.alignment = { horizontal: "right" };
                    }

                    worksheet[cellRef].s = style;
                }
            }

            // Aggiungi un bordo attorno al riepilogo
            for (let r = 0; r < 8; r++) {
                for (let c = 0; c < 2; c++) {
                    const cellRef = XLSX.utils.encode_cell({ r: r, c: c });
                    if (!worksheet[cellRef]) continue;

                    let style = worksheet[cellRef].s || {};
                    style.border = {
                        top: { style: r === 0 ? "medium" : "thin", color: { rgb: "4F81BD" } },
                        bottom: { style: r === 7 ? "medium" : "thin", color: { rgb: "4F81BD" } },
                        left: { style: c === 0 ? "medium" : "thin", color: { rgb: "4F81BD" } },
                        right: { style: c === 1 ? "medium" : "thin", color: { rgb: "4F81BD" } }
                    };

                    worksheet[cellRef].s = style;
                }
            }

            // Aggiungi il foglio al workbook
            const sheetName = modellista.email.substring(0, 30); // Limita la lunghezza del nome del foglio
            XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
        });

        // Crea un foglio riepilogativo con tutti i log
        if (allLogs.length > 0) {
            // Calcola statistiche complessive (solo per log completati)
            const completedLogs = allLogs.filter(log => log.logStatus === "Complete").length;
            const declinedBySpaarkly = allLogs.filter(log =>
                log.list_decline_variant &&
                log.list_decline_variant.some(d => d && (d.role === "ModellerSupervisor" || d.role === "Admin"))
            ).length;
            const declinedByClient = allLogs.filter(log =>
                log.list_decline_variant &&
                log.list_decline_variant.some(d => d && d.role === "Cliente")
            ).length;

            // Calcola il periodo di date
            const dateRange = filters.dateRange;
            const startDate = dateRange[0];
            const endDate = dateRange[1];
            const formattedStartDate = formatDate(startDate).split(' ')[0]; // Solo la data, non l'ora
            const formattedEndDate = formatDate(endDate).split(' ')[0]; // Solo la data, non l'ora

            // Crea la riga di intestazione con le statistiche complessive
            const summarySummaryRow = [
                ['Summary of All Logs', ''],
                ['Date Range', `${formattedStartDate} - ${formattedEndDate}`],
                ['Total Modellers', modellerCount],
                ['Total Glasses', totalGlasses],
                ['Declined by Spaarkly', declinedBySpaarkly],
                ['Declined by Client', declinedByClient],
                [''] // Riga vuota
            ];

            // Intestazioni per la tabella di riepilogo
            const headers = [
                'Product',
                'SKU',
                /* 'ID', */
                'Status',
                'Type of Modeling',
                'Modeling Note',
                'Completion Date',
                'Work Time First Validation',
                'Start Initialization Date',
                'End First Validation Date',
                'Work Complete Time',
                'Decline by Spaarkly',
                'Decline by Client',
                'Name'
            ];

            // Aggiungi l'intestazione della tabella solo una volta
            const summaryTableRows = [headers];

            // Ordina i log per data di inizializzazione (più recenti prima)
            allLogs.sort((a, b) => {
                const aDate = a.logInitVariantDate ?
                    (a.logInitVariantDate.seconds || a.logInitVariantDate._seconds || 0) : 0;
                const bDate = b.logInitVariantDate ?
                    (b.logInitVariantDate.seconds || b.logInitVariantDate._seconds || 0) : 0;

                return bDate - aDate;
            });

            // Aggiungi tutti i log in un'unica tabella
            allLogs.forEach(log => {
                // Calcola il tempo di prima validazione
                let firstValidationWork = "N/A";
                if (log.list_validate_variant && log.list_validate_variant.length > 0 && log.logInitVariantDate) {
                    const firstValidation = log.list_validate_variant[0].time;
                    firstValidationWork = calculateWorkTime(log.logInitVariantDate, firstValidation);
                }

                // Calcola il tempo di completamento
                let workComplete = "N/A";
                if (log.logInitVariantDate && log.logCompleteVariantDate) {
                    workComplete = calculateWorkTime(log.logInitVariantDate, log.logCompleteVariantDate);
                }

                // Formatta lo stato
                let statusText = "N/A";
                if (log.logStatus === "Complete") {
                    statusText = "Completed";
                } else {
                    statusText = log.logStatus || "Unknown";
                }

                const row = [
                    log.productName || 'N/A',
                    log.sku || 'N/A',
                    /* log.id || 'N/A', */
                    statusText,
                    log.list_validate_variant && log.list_validate_variant.length > 0 && log.list_validate_variant[0].type_of_modeling ? log.list_validate_variant[0].type_of_modeling : 'N/A',
                    log.list_validate_variant && log.list_validate_variant.length > 0 && log.list_validate_variant[0].modeling_note ? log.list_validate_variant[0].modeling_note : 'N/A',
                    formatDate(log.logCompleteVariantDate),
                    firstValidationWork,
                    formatDate(log.logInitVariantDate),
                    log.list_validate_variant && log.list_validate_variant.length > 0 ? formatDate(log.list_validate_variant[0].time) : 'N/A',
                    workComplete,
                    log.list_decline_variant ? log.list_decline_variant.filter(d => d && (d.role === "ModellerSupervisor" || d.role === "Admin")).length : 0,
                    log.list_decline_variant ? log.list_decline_variant.filter(d => d && d.role === "Cliente").length : 0,
                    log.modellerName || log.modellerEmail || 'N/A'
                ];

                summaryTableRows.push(row);
            });

            // Unisci i dati riassuntivi e la tabella
            const summarySheetData = [...summarySummaryRow, ...summaryTableRows];

            // Crea il foglio Excel di riepilogo
            const summaryWorksheet = XLSX.utils.aoa_to_sheet(summarySheetData);

            // Personalizza la larghezza delle colonne
            const summaryColWidths = [
                { wch: 22 }, // A - Product
                { wch: 18 }, // B - SKU
                /* { wch: 22 }, // C - ID */
                { wch: 12 }, // C - Status
                { wch: 20 }, // D - Type of Modeling
                { wch: 25 }, // E - Modeling Note
                { wch: 20 }, // F - Completion Date
                { wch: 22 }, // G - Work Time First Validation
                { wch: 22 }, // H - Start Initialization Date
                { wch: 22 }, // I - End First Validation Date
                { wch: 20 }, // J - Work Complete Time
                { wch: 20 }, // K - Decline by Spaarkly
                { wch: 20 }, // L - Decline by Client
                { wch: 30 }  // M - Name
            ];

            summaryWorksheet['!cols'] = summaryColWidths;

            // Aggiungi stili per migliorare la leggibilità

            // Stile per il titolo (A1)
            const titleCellRef = XLSX.utils.encode_cell({ r: 0, c: 0 });
            if (summaryWorksheet[titleCellRef]) {
                summaryWorksheet[titleCellRef].s = {
                    font: { bold: true, sz: 16 },
                    alignment: { vertical: "center", horizontal: "left" }
                };
            }

            // Stile per le intestazioni del riepilogo (A2:A7)
            for (let r = 1; r < 7; r++) {
                const cellRef = XLSX.utils.encode_cell({ r: r, c: 0 });
                if (!summaryWorksheet[cellRef]) continue;
                summaryWorksheet[cellRef].s = {
                    font: { bold: true, sz: 14 },
                    alignment: { vertical: "center", horizontal: "left" }
                };
            }

            // Stile per i valori del riepilogo (B2:B7)
            for (let r = 1; r < 7; r++) {
                const cellRef = XLSX.utils.encode_cell({ r: r, c: 1 });
                if (!summaryWorksheet[cellRef]) continue;
                summaryWorksheet[cellRef].s = {
                    font: { sz: 14 },
                    alignment: { vertical: "center", horizontal: "left" }
                };
            }

            // Stile per l'intestazione della tabella
            const summaryHeaderRow = summarySummaryRow.length; // riga dopo il riepilogo
            for (let c = 0; c < headers.length; c++) {
                const cellRef = XLSX.utils.encode_cell({ r: summaryHeaderRow, c: c });
                if (!summaryWorksheet[cellRef]) continue;
                summaryWorksheet[cellRef].s = {
                    font: { bold: true, color: { rgb: "FFFFFF" }, sz: 12 },
                    fill: { fgColor: { rgb: "4F81BD" } },
                    alignment: { vertical: "center", horizontal: "center" },
                    border: {
                        top: { style: "thin", color: { rgb: "000000" } },
                        bottom: { style: "thin", color: { rgb: "000000" } },
                        left: { style: "thin", color: { rgb: "000000" } },
                        right: { style: "thin", color: { rgb: "000000" } }
                    }
                };
            }

            // Aggiungi bordi alle celle della tabella
            for (let r = summaryHeaderRow + 1; r < summarySheetData.length; r++) {
                for (let c = 0; c < headers.length; c++) {
                    const cellRef = XLSX.utils.encode_cell({ r: r, c: c });
                    if (!summaryWorksheet[cellRef]) continue;

                    let style = {
                        border: {
                            top: { style: "thin", color: { rgb: "D3D3D3" } },
                            bottom: { style: "thin", color: { rgb: "D3D3D3" } },
                            left: { style: "thin", color: { rgb: "D3D3D3" } },
                            right: { style: "thin", color: { rgb: "D3D3D3" } }
                        }
                    };

                    // Allineamento specifico per alcune colonne
                    if (c === 3) { // Status column
                        style.alignment = { horizontal: "center" };
                    } else if (c >= 4 && c <= 9) { // Time and date columns
                        style.alignment = { horizontal: "right" };
                    }

                    summaryWorksheet[cellRef].s = style;
                }
            }

            // Aggiungi un bordo attorno al riepilogo del foglio di riepilogo
            for (let r = 0; r < 7; r++) {
                for (let c = 0; c < 2; c++) {
                    const cellRef = XLSX.utils.encode_cell({ r: r, c: c });
                    if (!summaryWorksheet[cellRef]) continue;

                    let style = summaryWorksheet[cellRef].s || {};
                    style.border = {
                        top: { style: r === 0 ? "medium" : "thin", color: { rgb: "4F81BD" } },
                        bottom: { style: r === 6 ? "medium" : "thin", color: { rgb: "4F81BD" } },
                        left: { style: c === 0 ? "medium" : "thin", color: { rgb: "4F81BD" } },
                        right: { style: c === 1 ? "medium" : "thin", color: { rgb: "4F81BD" } }
                    };

                    summaryWorksheet[cellRef].s = style;
                }
            }

            // Aggiungi il foglio di riepilogo al workbook come primo foglio
            XLSX.utils.book_append_sheet(workbook, summaryWorksheet, "All Log");

            // Riorganizza i fogli in modo che "All Log" sia il primo
            if (workbook.SheetNames.length > 1) {
                // Trova l'indice di "All Log"
                const allLogIndex = workbook.SheetNames.indexOf("All Log");

                // Se "All Log" non è già il primo foglio, riorganizza
                if (allLogIndex > 0) {
                    // Sposta "All Log" all'inizio dell'array di nomi
                    workbook.SheetNames.splice(allLogIndex, 1);
                    workbook.SheetNames.unshift("All Log");

                    // Riordina anche gli oggetti dei fogli se necessario
                    const allLogSheet = workbook.Sheets["All Log"];
                    delete workbook.Sheets["All Log"];

                    // Crea un nuovo oggetto Sheets con "All Log" come primo elemento
                    const newSheets = { "All Log": allLogSheet };

                    // Aggiungi gli altri fogli
                    workbook.SheetNames.slice(1).forEach(name => {
                        newSheets[name] = workbook.Sheets[name];
                    });

                    workbook.Sheets = newSheets;
                }
            }
        }

        return workbook;
    };

    // Genera e scarica il file Excel
    const generateExcelFile = async () => {
        try {
            // Ottieni i dati
            const data = await fetchLogsForExport();

            if (!data) {
                message.error("No data available for export. Please try again later.");
                return;
            }

            const { logs, modellisti } = data;

            if (logs.length === 0) {
                message.error("No logs found in the selected date range. Try selecting a different period.");
                return;
            }

            // Se non ci sono modellisti associati ai log
            if (modellisti.length === 0) {
                message.error("No modellers found in the system. Unable to generate the report.");
                return;
            }

            // Organizza i dati per modellista
            const modellistiData = organizeDataByModeller(logs, modellisti);

            // Verifica se dopo l'organizzazione c'è almeno un modellista con dei log
            const hasModellistaWithLogs = Object.values(modellistiData).some(data => data.logs.length > 0);

            if (!hasModellistaWithLogs) {
                message.error("No logs associated with modellers in the selected period.");
                return;
            }

            // Crea il workbook con i fogli per ogni modellista
            const workbook = createExcelSheets(modellistiData);

            // Genera e scarica il file Excel
            const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
            const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

            // Crea un URL per il download
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');

            // Formato del nome del file: LogVariant_dataInizio_dataFine.xlsx
            const startDate = filters.dateRange[0];
            const endDate = filters.dateRange[1];
            const startDateFormatted = formatDateForFileName(startDate);
            const endDateFormatted = formatDateForFileName(endDate);
            const fileName = `LogVariant_${startDateFormatted}_${endDateFormatted}.xlsx`;

            link.href = url;
            link.download = fileName;
            link.click();

            // Pulisci l'URL creato
            URL.revokeObjectURL(url);

            message.success("Download completed successfully");
            handleCloseModal();
        } catch (error) {
            console.error("Error generating Excel file:", error);

            // Messaggi di errore più specifici in base al tipo di errore
            if (error.message && error.message.includes("Cannot read properties")) {
                message.error("Error reading data: missing or incomplete data in the selected period.");
            } else if (!filters.dateRange || filters.dateRange.length !== 2) {
                message.error("Invalid date range. Please select a valid period.");
            } else {
                message.error("An error occurred while generating the file. Try with a different period.");
            }
        }
    };

    // Gestisce la conferma del download
    const handleConfirmDownload = () => {
        generateExcelFile();
    };

    return (
        <>
            <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={showModal}
            >
                Download Log
            </Button>

            <MediumModal
                visible={isModalVisible}
                onClose={handleCloseModal}
                title="Download Log"
                onConfirm={handleConfirmDownload}
                cancelText="Cancel"
                confirmText="Download"
            >
                <div>
                    <p style={{ fontSize: 'clamp(0.875rem, 2.5vw, 1rem)', marginBottom: '1rem' }}>
                        You are about to download an Excel file containing completed modeller logs for the selected period.
                    </p>
                    <p style={{ fontSize: 'clamp(0.75rem, 2vw, 0.875rem)' }}>
                        The file will contain one sheet for each modeller with:
                    </p>
                    <ul style={{ marginLeft: '1.5rem', fontSize: 'clamp(0.75rem, 2vw, 0.875rem)', marginBottom: '1rem' }}>
                        <li>A summary with the number of completed glasses and the total first validation time</li>
                        <li>A detailed table of all completed logs associated with the modeller</li>
                    </ul>
                    <p style={{ fontSize: 'clamp(0.75rem, 2vw, 0.875rem)', fontStyle: 'italic' }}>
                        Note: If the selected period is extensive, the generation process may take a few moments.
                    </p>
                </div>
            </MediumModal>
        </>
    );
};

export default DownloadLog;